import { useState, useEffect } from 'react';
import { credentialsApi, usersApi, teamsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Loader2, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';

interface CredentialShare {
  id: string;
  sharedWithInternalReferenceId: string;
  sharedWithDisplayName: string;
  sharedWithEmail: string;
  createdAt: string;
}

interface CredentialTeamShare {
  id: string;
  teamId: string;
  teamName: string;
  memberCount: number;
  isAllMembers: boolean;
  createdAt: string;
}

interface SubscriptionUser {
  id: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  internalReferenceId: string;
}

interface Team {
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  isAllMembers?: boolean;
}

interface Credential {
  id: string;
  name: string;
  type: string;
  isOwner?: boolean;
  shareCount?: number;
}

interface ShareCredentialDialogProps {
  credential: Credential | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSharesUpdated: () => void;
}

export function ShareCredentialDialog({
  credential,
  open,
  onOpenChange,
  onSharesUpdated,
}: ShareCredentialDialogProps) {
  const [activeTab, setActiveTab] = useState<'users' | 'teams'>('users');

  // User sharing state
  const [users, setUsers] = useState<SubscriptionUser[]>([]);
  const [currentShares, setCurrentShares] = useState<CredentialShare[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isSharedWithAll, setIsSharedWithAll] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Team sharing state
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeamShares, setCurrentTeamShares] = useState<CredentialTeamShare[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [teamSearchQuery, setTeamSearchQuery] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load users, teams, and current shares when dialog opens
  useEffect(() => {
    if (open && credential) {
      loadData();
    }
  }, [open, credential?.id]);

  const loadData = async () => {
    if (!credential) return;

    setLoading(true);
    try {
      const [usersRes, teamsRes, sharesRes] = await Promise.all([
        usersApi.getSubscriptionUsers(),
        teamsApi.list(),
        credentialsApi.getShares(credential.id),
      ]);

      setUsers(usersRes.data || []);
      setTeams(teamsRes.data || []);
      setCurrentShares(sharesRes.data?.shares || []);
      setCurrentTeamShares(sharesRes.data?.teamShares || []);
      setIsSharedWithAll(sharesRes.data?.isSharedWithAll || false);

      // Initialize user selections from current shares
      setSelectedUserIds(
        new Set((sharesRes.data?.shares || []).map((s: CredentialShare) => s.sharedWithInternalReferenceId))
      );

      // Initialize team selections from current team shares
      setSelectedTeamIds(
        new Set((sharesRes.data?.teamShares || []).map((s: CredentialTeamShare) => s.teamId))
      );
    } catch (error: any) {
      console.error('Failed to load sharing data:', error);
      toast.error('Failed to load sharing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!credential) return;

    setSaving(true);
    try {
      // Handle user shares
      const currentUserIds = new Set(currentShares.map((s) => s.sharedWithInternalReferenceId));
      const usersToAdd = [...selectedUserIds].filter((id) => !currentUserIds.has(id));
      const usersToRemove = [...currentUserIds].filter((id) => !selectedUserIds.has(id));

      // Always call share API to update isSharedWithAll flag
      await credentialsApi.share(credential.id, usersToAdd, isSharedWithAll);

      // Only unshare individual users if not sharing with all
      if (!isSharedWithAll) {
        for (const userId of usersToRemove) {
          await credentialsApi.unshare(credential.id, userId);
        }
      }

      // Handle team shares
      const currentTeamIds = new Set(currentTeamShares.map((s) => s.teamId));
      const teamsToAdd = [...selectedTeamIds].filter((id) => !currentTeamIds.has(id));
      const teamsToRemove = [...currentTeamIds].filter((id) => !selectedTeamIds.has(id));

      // Add new team shares
      if (teamsToAdd.length > 0) {
        await credentialsApi.shareWithTeams(credential.id, teamsToAdd);
      }

      // Remove team shares
      for (const teamId of teamsToRemove) {
        await credentialsApi.unshareFromTeam(credential.id, teamId);
      }

      toast.success('Sharing updated');
      onSharesUpdated();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to update sharing:', error);
      toast.error(error?.response?.data?.error || 'Failed to update sharing');
    } finally {
      setSaving(false);
    }
  };

  // User selection handlers
  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const selectAllUsers = () => {
    setSelectedUserIds(new Set(users.map((u) => u.internalReferenceId)));
  };

  const deselectAllUsers = () => {
    setSelectedUserIds(new Set());
  };

  // Team selection handlers
  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  const selectAllTeams = () => {
    setSelectedTeamIds(new Set(teams.map((t) => t.id)));
  };

  const deselectAllTeams = () => {
    setSelectedTeamIds(new Set());
  };

  // Filtered users
  const filteredUsers = users.filter(
    (user) =>
      user.displayName?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  const sharedUsers = filteredUsers.filter((user) => selectedUserIds.has(user.internalReferenceId));
  const availableUsers = filteredUsers.filter((user) => !selectedUserIds.has(user.internalReferenceId));

  // Filtered teams
  const filteredTeams = teams.filter(
    (team) =>
      team.name?.toLowerCase().includes(teamSearchQuery.toLowerCase()) ||
      team.description?.toLowerCase().includes(teamSearchQuery.toLowerCase())
  );

  const sharedTeams = filteredTeams.filter((team) => selectedTeamIds.has(team.id));
  const availableTeams = filteredTeams.filter((team) => !selectedTeamIds.has(team.id));

  const selectedUserCount = selectedUserIds.size;
  const selectedTeamCount = selectedTeamIds.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share Credential</DialogTitle>
          <DialogDescription>
            Share this credential with users or teams. They can use it in workflows but cannot view the details.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'users' | 'teams')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users {selectedUserCount > 0 && `(${selectedUserCount})`}
            </TabsTrigger>
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Teams {selectedTeamCount > 0 && `(${selectedTeamCount})`}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-3 mt-4">
            {/* All Users Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Share with</span>
              <div className="flex items-center gap-2">
                <label htmlFor="share-with-all" className="text-xs text-muted-foreground cursor-pointer">
                  All users (incl. future)
                </label>
                <Switch
                  id="share-with-all"
                  checked={isSharedWithAll}
                  onCheckedChange={setIsSharedWithAll}
                />
              </div>
            </div>

            {/* Individual user selection */}
            <div className={isSharedWithAll ? 'opacity-50 pointer-events-none' : ''}>
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>

              {/* User Lists */}
              {loading ? (
                <div className="flex items-center justify-center h-[200px] border rounded-md">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="h-[200px] border rounded-md flex items-center justify-center">
                  <div className="text-center text-sm text-muted-foreground">
                    {userSearchQuery ? 'No users found matching your search' : 'No users available to share with'}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {/* Currently Shared */}
                  <div className="border rounded-md">
                    <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Currently Shared ({sharedUsers.length})
                      </span>
                      {selectedUserIds.size > 0 && (
                        <button
                          type="button"
                          onClick={deselectAllUsers}
                          className="text-xs text-primary hover:underline"
                        >
                          Deselect All
                        </button>
                      )}
                    </div>
                    <ScrollArea className="h-[180px]">
                      {sharedUsers.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          Not shared with anyone yet
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {sharedUsers.map((user) => (
                            <div
                              key={user.internalReferenceId}
                              className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                              onClick={() => toggleUser(user.internalReferenceId)}
                            >
                              <Checkbox
                                checked={selectedUserIds.has(user.internalReferenceId)}
                                onCheckedChange={() => toggleUser(user.internalReferenceId)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{user.displayName}</div>
                                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>

                  {/* Available Users */}
                  <div className="border rounded-md">
                    <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Available Users ({availableUsers.length})
                      </span>
                      {availableUsers.length > 0 && (
                        <button
                          type="button"
                          onClick={selectAllUsers}
                          className="text-xs text-primary hover:underline"
                        >
                          Select All
                        </button>
                      )}
                    </div>
                    <ScrollArea className="h-[180px]">
                      {availableUsers.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          {userSearchQuery ? 'No matches' : 'All users have access'}
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {availableUsers.map((user) => (
                            <div
                              key={user.internalReferenceId}
                              className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                              onClick={() => toggleUser(user.internalReferenceId)}
                            >
                              <Checkbox
                                checked={selectedUserIds.has(user.internalReferenceId)}
                                onCheckedChange={() => toggleUser(user.internalReferenceId)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{user.displayName}</div>
                                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>
              )}

              {!isSharedWithAll && selectedUserCount > 0 && (
                <div className="text-xs text-muted-foreground mt-2">
                  {selectedUserCount} user{selectedUserCount !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" className="space-y-3 mt-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search teams..."
                value={teamSearchQuery}
                onChange={(e) => setTeamSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Team Lists */}
            {loading ? (
              <div className="flex items-center justify-center h-[200px] border rounded-md">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredTeams.length === 0 ? (
              <div className="h-[200px] border rounded-md flex items-center justify-center">
                <div className="text-center text-sm text-muted-foreground">
                  {teamSearchQuery ? 'No teams found matching your search' : 'No teams available to share with'}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {/* Currently Shared Teams */}
                <div className="border rounded-md">
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Currently Shared ({sharedTeams.length})
                    </span>
                    {selectedTeamIds.size > 0 && (
                      <button
                        type="button"
                        onClick={deselectAllTeams}
                        className="text-xs text-primary hover:underline"
                      >
                        Deselect All
                      </button>
                    )}
                  </div>
                  <ScrollArea className="h-[180px]">
                    {sharedTeams.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Not shared with any teams yet
                      </div>
                    ) : (
                      <div className="p-2 space-y-1">
                        {sharedTeams.map((team) => (
                          <div
                            key={team.id}
                            className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                            onClick={() => toggleTeam(team.id)}
                          >
                            <Checkbox
                              checked={selectedTeamIds.has(team.id)}
                              onCheckedChange={() => toggleTeam(team.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{team.name}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {team.isAllMembers ? 'All users' : `${team.memberCount} member${team.memberCount !== 1 ? 's' : ''}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Available Teams */}
                <div className="border rounded-md">
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Available Teams ({availableTeams.length})
                    </span>
                    {availableTeams.length > 0 && (
                      <button
                        type="button"
                        onClick={selectAllTeams}
                        className="text-xs text-primary hover:underline"
                      >
                        Select All
                      </button>
                    )}
                  </div>
                  <ScrollArea className="h-[180px]">
                    {availableTeams.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        {teamSearchQuery ? 'No matches' : 'All teams have access'}
                      </div>
                    ) : (
                      <div className="p-2 space-y-1">
                        {availableTeams.map((team) => (
                          <div
                            key={team.id}
                            className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                            onClick={() => toggleTeam(team.id)}
                          >
                            <Checkbox
                              checked={selectedTeamIds.has(team.id)}
                              onCheckedChange={() => toggleTeam(team.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{team.name}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {team.isAllMembers ? 'All users' : `${team.memberCount} member${team.memberCount !== 1 ? 's' : ''}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            )}

            {selectedTeamCount > 0 && (
              <div className="text-xs text-muted-foreground mt-2">
                {selectedTeamCount} team{selectedTeamCount !== 1 ? 's' : ''} selected
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
